import { useCallback, useEffect, useState } from "react";
import { formatRelativeTime } from "@saucer/core";
import type { Invite, Visibility } from "@saucer/core";
import { api, createToken, listTokens, revokeToken } from "../api.js";
import type { TokenSummary } from "../api.js";
import type { Session } from "../app.js";

export function Settings({ session }: { session: NonNullable<Session> }) {
  return (
    <main className="stack narrow">
      <section className="stack stack--tight">
        <h1>Settings</h1>
        <p className="meta">
          Signed in as {session.user.username}
          {session.user.isAdmin ? " · admin" : ""}. Your files live at saucer.sh/
          {session.user.username}/…
        </p>
      </section>

      <Privacy />
      <Tokens />
      {session.user.isAdmin ? <Invites /> : null}
    </main>
  );
}

function Privacy() {
  const [current, setCurrent] = useState<Visibility | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api.getPrivacy().then(({ defaultVisibility }) => setCurrent(defaultVisibility));
  }, []);

  const choose = async (visibility: Visibility) => {
    if (busy || visibility === current) return;
    setBusy(true);
    try {
      setCurrent((await api.setPrivacy(visibility)).defaultVisibility);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="stack stack--tight">
      <div className="section-head">
        <h2>Privacy</h2>
        <span className="meta">
          What an upload becomes when nothing says otherwise — the same default `saucer
          privacy` sets. Each file keeps its own toggle on the dashboard.
        </span>
      </div>

      <span className="seg" role="group" aria-label="Default visibility for new uploads">
        <span className="seg__label mono">new uploads open for</span>
        <button
          className={`seg__option${current === "public" ? " seg__option--on" : ""}`}
          type="button"
          disabled={busy || current === null}
          title="Anyone who has the link can open it"
          onClick={() => choose("public")}
        >
          anyone
        </button>
        <button
          className={`seg__option${current === "private" ? " seg__option--on" : ""}`}
          type="button"
          disabled={busy || current === null}
          title="Links only open while you are signed in; everyone else gets a 404"
          onClick={() => choose("private")}
        >
          only you
        </button>
      </span>
    </section>
  );
}

function Tokens() {
  const [tokens, setTokens] = useState<TokenSummary[]>([]);
  const [name, setName] = useState("");
  const [fresh, setFresh] = useState<string | null>(null);

  const load = useCallback(async () => setTokens(await listTokens()), []);
  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="stack stack--tight">
      <div className="section-head">
        <h2>CLI tokens</h2>
        <span className="meta">Used by `saucer send` and by CI.</span>
      </div>

      {fresh ? (
        <div className="notice">
          <p>Copy this now — it is not shown again.</p>
          <code className="mono token">{fresh}</code>
        </div>
      ) : null}

      <form
        className="row"
        onSubmit={async (event) => {
          event.preventDefault();
          if (!name.trim()) return;
          const token = await createToken(name.trim());
          setName("");
          setFresh(token.secret);
          await load();
        }}
      >
        <input
          className="input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder="What is this token for?"
        />
        <button className="button" type="submit">
          Create token
        </button>
      </form>

      <ul className="rows">
        {tokens.map((token) => (
          <li key={token.id} className="rows__row">
            <span>{token.name}</span>
            <span className="meta">
              {token.lastUsedAt
                ? `used ${formatRelativeTime(token.lastUsedAt)}`
                : `created ${formatRelativeTime(token.createdAt)}`}
            </span>
            <button
              className="button button--quiet button--danger"
              type="button"
              onClick={async () => {
                await revokeToken(token.id);
                await load();
              }}
            >
              Revoke
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Invites() {
  const [invites, setInvites] = useState<Invite[]>([]);
  const [note, setNote] = useState("");

  const load = useCallback(async () => setInvites((await api.listInvites()).invites), []);
  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="stack stack--tight">
      <div className="section-head">
        <h2>Invites</h2>
        <span className="meta">One code, one account.</span>
      </div>

      <form
        className="row"
        onSubmit={async (event) => {
          event.preventDefault();
          await api.createInvites(1, note.trim() || undefined);
          setNote("");
          await load();
        }}
      >
        <input
          className="input"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Who is this for?"
        />
        <button className="button" type="submit">
          Mint a code
        </button>
      </form>

      <ul className="rows">
        {invites.map((invite) => (
          <li key={invite.code} className="rows__row">
            <code className="mono">{invite.code}</code>
            <span className="meta">
              {invite.redeemedBy ? `taken by ${invite.redeemedBy}` : (invite.note ?? "open")}
            </span>
            <button
              className="button button--quiet"
              type="button"
              disabled={Boolean(invite.redeemedBy)}
              onClick={() => navigator.clipboard.writeText(invite.code)}
            >
              Copy
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
